import React from "react";

import { NotifierContextProvider } from "react-headless-notifier";
import { createBrowserRouter, RouterProvider } from "react-router-dom";

import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import Parts from "./pages/Scrape";
import { sentryDSN } from "./config";
import Layout from "./layouts/Layout";
import Login from "./pages/public/Login";
import Dashboard from "./pages/Dashboard";
import { Customers } from "./pages/Customers";
import Reports from "./pages/Reports/Reports";
import Categories from "./pages/Categories.tsx";
import { ViewCustomer } from "./pages/Customer";
import reportWebVitals from "./reportWebVitals";
import RequireAuth from "./components/RequireAuth";
import { Applications } from "./pages/Applications";
import { ViewApplication } from "./pages/Application";
import ErrorBoundary from "./pages/Error/ErrorBoundry";
import { ReportForm } from "./pages/Reports/ReportForm";
import SearchTermsPage from "./pages/Scrape/SearchTerms";
import SellerHistory from "./pages/Scrape/SellerHistory";
import AiReview from "./pages/Scrape/AiReview";
import { NewCustomer } from "./pages/Customers/NewCustomer";
import { EditCustomer } from "./pages/Customer/EditCustomer";
import { CustomerReadiness } from "./pages/Customer/Readiness";
import { PageNameProvider } from "./contexts/PageTitleContext";
import EditCategory from "./pages/Categories.tsx/EditCategory";
import PendingCategories from "./pages/PendingCategories.tsx";
import EditPendingCategory from "./pages/PendingCategories.tsx/EditPendingCategory";
import { NewApplication } from "./pages/Applications/NewApplication";
import { EditApplication } from "./pages/Application/EditApplication";
import TicketsPage from "./pages/Tickets";
import ViewTicketPage from "./pages/Tickets/ViewTicket";
import NewTicketPage from "./pages/Tickets/NewTicket";
import TicketInsightsPage from "./pages/Tickets/Insights";
import CategoryGroups from "./pages/CategoryGroups.tsx";
import FeaturesPage from "./pages/Features";
import TemplatesPage from "./pages/Templates";
import SettingDefinitionsPage from "./pages/SettingDefinitions";
import SyncTrackerPage from "./pages/SyncTracker";
import PinnacleMappingPage from "./pages/PinnacleMapping";
import ToolsPage from "./pages/Tools";
import CoreDeploymentsPage from "./pages/CoreDeployments";
import MvlPage from "./pages/Mvl";
import ProfilePage from "./pages/Profile";
import TitleConfigurationPage from "./pages/TitleConfiguration";
import InitialPriceEstimatePage from "./pages/InitialPriceEstimate";

import "./index.css";

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

Sentry.init({
  dsn: sentryDSN,
  sendDefaultPii: true,
});

// React Router: Append your pages to the router here
const router = createBrowserRouter([
  { path: "/login", element: <Login />, errorElement: <ErrorBoundary /> },
  {
    path: "/",
    element: <Layout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        element: <RequireAuth />,
        children: [
          {
            element: <Dashboard />,
            index: true,
          },
          {
            element: <Customers />,
            path: "customers",
          },
          {
            element: <NewCustomer />,
            path: "customers/new",
          },
          {
            element: <ViewCustomer />,
            path: "customer/:customerId",
          },
          {
            element: <EditCustomer />,
            path: "customer/edit/:customerId",
          },
          {
            element: <CustomerReadiness />,
            path: "customer/:customerId/readiness",
          },
          {
            element: <Applications />,
            path: "applications",
          },
          {
            element: <NewApplication />,
            path: "applications/new",
          },
          {
            element: <ViewApplication />,
            path: "application/:applicationId",
          },
          {
            element: <EditApplication />,
            path: "application/edit/:applicationId",
          },
          {
            path: "categories",
            children: [
              {
                index: true,
                element: <Categories />,
              },
              {
                path: ":categoryId",
                element: <EditCategory />,
              }
            ]
          },
          {
            // Its own top-level route rather than under categories/, so the sidebar's prefix
            // matching does not light up Category Editor as well.
            element: <PinnacleMappingPage />,
            path: "pinnacle-mapping",
          },
          {
            element: <CategoryGroups />,
            path: "category-groups",
          },
          {
            path: "title-configuration",
            children: [
              {
                index: true,
                element: <TitleConfigurationPage />,
              },
              {
                path: ":categoryId",
                element: <TitleConfigurationPage />,
              },
            ],
          },
          {
            path: "initial-price-estimate",
            children: [
              {
                index: true,
                element: <InitialPriceEstimatePage />,
              },
              {
                path: ":categoryId",
                element: <InitialPriceEstimatePage />,
              },
            ],
          },
          {
            path: "pending-categories",
            children: [
              {
                index: true,
                element: <PendingCategories />,
              },
              {
                path: ":pendingCategoryId",
                element: <EditPendingCategory />,
              }
            ]
          },
          {
            element: <FeaturesPage />,
            path: "features",
          },
          {
            element: <TemplatesPage />,
            path: "templates",
          },
          {
            element: <SettingDefinitionsPage />,
            path: "settings",
          },
          {
            element: <SyncTrackerPage />,
            path: "settings/sync-tracker",
          },
          {
            element: <ToolsPage />,
            path: "tools",
          },
          {
            element: <CoreDeploymentsPage />,
            path: "deployments",
          },
          {
            element: <MvlPage />,
            path: "mvl",
          },
          {
            element: <ProfilePage />,
            path: "profile",
          },
          {
            element: <Parts />,
            path: "scrape",
          },
          {
            element: <Reports />,
            path: "reports",
          },
          {
            element: <ReportForm />,
            path: "reports/new",
          },
          {
            element: <ReportForm />,
            path: "reports/:reportId/edit",
          },
          {
            element: <SearchTermsPage />,
            path: "scrape/search-terms",
          },
          {
            element: <SellerHistory />,
            path: "scrape/seller-history",
          },
          {
            element: <AiReview />,
            path: "scrape/ai-review",
          },
          {
            element: <TicketsPage />,
            path: "tickets",
          },
          {
            element: <NewTicketPage />,
            path: "tickets/new",
          },
          {
            element: <TicketInsightsPage />,
            path: "tickets/insights",
          },
          {
            element: <ViewTicketPage />,
            path: "tickets/:ticketId",
          },
        ],
      },
    ],
  },
]);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
    },
  },
});

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <PageNameProvider>
        <NotifierContextProvider
          config={{
            position: "topRight",
            zIndex: 99999,
            className: "notification-container",
          }}
        >
          <RouterProvider router={router} />
          <ReactQueryDevtools initialIsOpen={false} />
        </NotifierContextProvider>
      </PageNameProvider>
    </QueryClientProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
